// @flow
import { forwardRef, type Element } from 'react';
import LazyLoad from 'react-lazyload';
import type { AuthorType, CategoryType } from 'components/types';
import { getFormatDate, getPrintTime } from 'components/PageContext';
import TruncatedText from 'components/TruncatedText';
import { LAZY_LOAD_OFFSET, MOBILE_BREAKPOINT } from 'utils/theme';
import useMediaQuery from 'utils/useMediaQuery';
import { safeLink } from 'utils/safeLink';
import { FULL_TOP_IMAGE_CLASS } from 'styled/Card';
import {
  CardCategory,
  CardCategoryRow,
  Card,
  CardPositioner,
  CardAuthor,
  CardText,
  CardTitle,
  CardPublishedDate,
  CardImage,
  CardTextContent,
  CardPersonImage,
  CardFooter,
  CARD_FORMATS,
  IMAGE_HEIGHTS
} from './styled';

// eslint-disable-next-line flowtype/require-types-at-top
type PropsType = {
  author: AuthorType,
  categories?: CategoryType[],
  /**
   *  Used for adding "siblings" to the card.
   *  Should be used for debug values or tooltips and any content should
   *  be absolutely positioned as to not interfere with the card display
   */
  children?: Element<*>,
  className?: string,
  format: $Values<typeof CARD_FORMATS>,
  image: string,
  publishedDate: string,
  readTime?: number,
  showAuthor?: boolean,
  showCategories?: boolean,
  showPreviewText?: boolean,
  showPublishedDate?: boolean,
  showReadTime?: boolean,
  slug: string,
  style?: { [string]: string | number },
  text?: string,
  title: string
};

const Preview = (
  {
    format,
    slug,
    publishedDate,
    author,
    categories,
    title,
    image,
    text,
    readTime,
    className,
    showAuthor,
    showCategories,
    showPublishedDate,
    showReadTime,
    showPreviewText,
    children,
    style
  }: PropsType,
  ref: any
) => {
  let footer = null;
  const imageOptions = {
    size: {
      width: 0,
      height:
        4 * // We use 4 times the desired height to get the images showing at a nice resolution
        useMediaQuery({
          [MOBILE_BREAKPOINT]: IMAGE_HEIGHTS.mobile,
          // I think this is due to the extra "mobile" key which is not a CARD_FORMATS value
          // $FlowFixMe
          default: IMAGE_HEIGHTS[format] || IMAGE_HEIGHTS[CARD_FORMATS.THIRD]
        })
    }
  };
  const printTime = getPrintTime();
  const formatDate = getFormatDate();
  if (showAuthor || showPublishedDate || showReadTime) {
    let publishedNode = null;
    if (showPublishedDate && showReadTime) {
      publishedNode = (
        <CardPublishedDate>
          {formatDate(publishedDate).toUpperCase()} &middot;{' '}
          {printTime(readTime)}
        </CardPublishedDate>
      );
    } else if (showPublishedDate) {
      publishedNode = (
        <CardPublishedDate>
          {formatDate(publishedDate).toUpperCase()}
        </CardPublishedDate>
      );
    } else {
      publishedNode = (
        <CardPublishedDate>{printTime(readTime)}</CardPublishedDate>
      );
    }
    if (showAuthor) {
      const { name, image: authorImage } = author;
      footer = (
        <CardFooter>
          <CardPersonImage alt={name} src={authorImage} />
          <div>
            <CardAuthor>{name}</CardAuthor>
            {publishedNode}
          </div>
        </CardFooter>
      );
    } else {
      footer = <CardFooter>{publishedNode}</CardFooter>;
    }
  }
  return (
    <CardPositioner format={format} ref={ref} style={style}>
      <Card
        className={className}
        format={format}
        to={safeLink(slug)}
        showFooter={showReadTime || showPublishedDate || showAuthor}
        showPreview={showPreviewText}
      >
        <LazyLoad offset={LAZY_LOAD_OFFSET}>
          <CardImage
            className={FULL_TOP_IMAGE_CLASS}
            format={format}
            src={image}
            options={imageOptions}
          />
        </LazyLoad>
        <CardTextContent>
          {showCategories && (
            <CardCategoryRow>
              {(categories || []).map((category) => {
                if (!category.color || !category.name) {
                  return null;
                }
                return (
                  <CardCategory key={category.uuid} color={category.color}>
                    {category.name}
                  </CardCategory>
                );
              })}
            </CardCategoryRow>
          )}
          <TruncatedText Component={CardTitle} text={title} />
          {showPreviewText && (
            <TruncatedText Component={CardText} text={text || ''} />
          )}
          {footer}
        </CardTextContent>
      </Card>
      {children}
    </CardPositioner>
  );
};

export default forwardRef<PropsType, any>(Preview);
